import { createServer, type IncomingMessage, type Server, type ServerResponse } from "node:http";
import open from "open";
import { CLIENT_ID, DEFAULT_SCOPE } from "../constants.js";
import type { ResolvedConfig } from "../config.js";
import { generatePkce } from "./pkce.js";
import { finalizeLogin } from "./session.js";
import { CliApiClient } from "../api/cli-api.js";
import { formatPlatformLabel } from "../utils/platform.js";
import { printInfo, printSuccess } from "../utils/output.js";

const LOGIN_TIMEOUT_MS = 300_000;

export async function browserPkceLogin(config: ResolvedConfig): Promise<void> {
  const pkce = generatePkce();
  const port = await pickPort();
  const redirectUri = `http://127.0.0.1:${port}/callback/${pkce.nonce}`;
  const loginUrl = new URL(`${config.platformUrl}/cli/login`);
  loginUrl.searchParams.set("client_id", CLIENT_ID);
  loginUrl.searchParams.set("redirect_uri", redirectUri);
  loginUrl.searchParams.set("response_type", "code");
  loginUrl.searchParams.set("code_challenge", pkce.codeChallenge);
  loginUrl.searchParams.set("code_challenge_method", "S256");
  loginUrl.searchParams.set("state", pkce.state);
  loginUrl.searchParams.set("scope", DEFAULT_SCOPE);

  const code = await waitForCallback(port, pkce.nonce, pkce.state, loginUrl.toString());
  const client = new CliApiClient(config);
  const token = await client.oauthToken({
    grant_type: "authorization_code",
    client_id: CLIENT_ID,
    code,
    code_verifier: pkce.codeVerifier,
    redirect_uri: redirectUri,
  });

  await finalizeLogin(config, token);
  const display = token.user?.email ?? token.user?.display_name ?? "user";
  printSuccess(`Logged in as ${display} (${formatPlatformLabel(config.platformUrl)})`);
}

async function pickPort(): Promise<number> {
  return new Promise((resolve, reject) => {
    const server = createServer();
    server.listen(0, "127.0.0.1", () => {
      const addr = server.address();
      if (!addr || typeof addr === "string") {
        server.close();
        reject(new Error("Failed to bind callback port"));
        return;
      }
      const port = addr.port;
      server.close(() => resolve(port));
    });
    server.on("error", reject);
  });
}

function waitForCallback(
  port: number,
  nonce: string,
  expectedState: string,
  loginUrl: string,
): Promise<string> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let server: Server;

    const stopServer = () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      server.closeAllConnections?.();
      server.close();
    };

    const finish = (handler: () => void) => {
      if (settled) return;
      settled = true;
      stopServer();
      handler();
    };

    server = createServer(async (req: IncomingMessage, res: ServerResponse) => {
      try {
        const url = new URL(req.url ?? "/", `http://127.0.0.1:${port}`);
        if (url.pathname !== `/callback/${nonce}`) {
          res.writeHead(404);
          res.end("Not found");
          return;
        }
        const code = url.searchParams.get("code");
        const state = url.searchParams.get("state");
        if (!code || state !== expectedState) {
          res.writeHead(400);
          res.end("Invalid callback");
          finish(() =>
            reject(new Error("Invalid OAuth callback: state mismatch or missing code")),
          );
          return;
        }
        res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" });
        res.end("<html><body><h2>Login successful. You can close this window.</h2></body></html>");
        finish(() => resolve(code));
      } catch (err) {
        finish(() => reject(err));
      }
    });

    server.listen(port, "127.0.0.1", () => {
      printInfo("Opening browser to authorize AgentScope Platform CLI...");
      printInfo(`If the browser does not open, visit:\n  ${loginUrl}`);
      printInfo("Waiting for authorization...");
      void open(loginUrl).catch(() => {});
    });

    timeoutId = setTimeout(() => {
      finish(() => reject(new Error("Login timed out")));
    }, LOGIN_TIMEOUT_MS);
  });
}
